import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { INTEGRATION_CONFIG } from '../types/integration.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for IntegrationConfig entity.
 * Stored per organization in 'org/{orgId}/integrations'.
 *
 * Defines the metadata for integration configurations that are
 * managed by organization administrators.
 */
export const integrationConfigDescriptor = {
    name: INTEGRATION_CONFIG,
    label: 'Integração',
    domain: 'integration',
    collectionPath: 'org/{orgId}/integrations',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'External integration configuration for REST APIs and other services',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique identifier',
        },
        {
            name: 'name',
            label: 'Nome',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Unique key used to reference this integration (e.g., erp-sync)',
        },
        {
            name: 'label',
            label: 'Rótulo',
            type: 'string',
            required: true,
            description: 'Human-readable label',
        },
        {
            name: 'type',
            label: 'Tipo',
            type: 'enum',
            required: true,
            enumValues: ['REST'],
            description: 'Integration protocol type',
        },
        {
            name: 'baseUrl',
            label: 'URL base',
            type: 'string',
            required: true,
            description: 'Base URL for the integration endpoint',
        },
        {
            name: 'auth',
            label: 'Autenticação',
            type: 'object',
            required: true,
            description: 'Authentication configuration',
            objectFields: [
                {
                    name: 'type',
                    label: 'Tipo de autenticação',
                    type: 'enum',
                    required: true,
                    enumValues: ['none', 'bearer', 'api-key', 'basic', 'session-token'],
                    description: 'Authentication strategy',
                },
                {
                    name: 'token',
                    label: 'Token bearer',
                    type: 'string',
                    required: false,
                    description: 'Static bearer token (for bearer type)',
                },
                {
                    name: 'apiKeyName',
                    label: 'Nome da chave de API',
                    type: 'string',
                    required: false,
                    description: 'API key header or query param name (for api-key type)',
                },
                {
                    name: 'apiKeyValue',
                    label: 'Valor da chave de API',
                    type: 'string',
                    required: false,
                    description: 'API key value (for api-key type)',
                },
                {
                    name: 'apiKeyLocation',
                    label: 'Local da chave de API',
                    type: 'enum',
                    required: false,
                    enumValues: ['header', 'query'],
                    description: 'Where to send the API key (for api-key type)',
                },
                {
                    name: 'username',
                    label: 'Usuário',
                    type: 'string',
                    required: false,
                    description: 'Username (for basic type)',
                },
                {
                    name: 'password',
                    label: 'Senha',
                    type: 'string',
                    required: false,
                    description: 'Password (for basic type)',
                },
            ],
        },
        {
            name: 'retry',
            label: 'Configuração de retry',
            type: 'object',
            required: false,
            description: 'Retry configuration for failed requests',
            objectFields: [
                {
                    name: 'enabled',
                    label: 'Ativo',
                    type: 'boolean',
                    required: true,
                    description: 'Whether retry is enabled',
                },
                {
                    name: 'maxAttempts',
                    label: 'Máximo de tentativas',
                    type: 'number',
                    required: false,
                    description: 'Maximum number of retry attempts (default: 3)',
                },
                {
                    name: 'delayMs',
                    label: 'Atraso (ms)',
                    type: 'number',
                    required: false,
                    description: 'Delay between retries in milliseconds (default: 1000)',
                },
            ],
        },
        {
            name: 'timeoutMs',
            label: 'Timeout (ms)',
            type: 'number',
            required: false,
            description: 'Request timeout in milliseconds (default: 30000)',
        },
        {
            name: 'defaultHeaders',
            label: 'Cabeçalhos padrão',
            type: 'object',
            required: false,
            description: 'Default headers sent with every request',
        },
        {
            name: 'enabled',
            label: 'Ativo',
            type: 'boolean',
            required: true,
            defaultValue: true,
            description: 'Whether the integration is active',
        },
    ],
};
//# sourceMappingURL=integration.descriptor.js.map